/**
 * 与 FastAPI 后端通信的轻量封装（相对路径，生产环境由同域 / Vite 代理转发）
 */

function jsonHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('auth_token');
  if (token) {
    h['Authorization'] = `Bearer ${token}`;
  }
  return h;
}

/** 将第二参数解析为查询对象：支持 get(url, { page: 1 }) 与 get(url, { params: { ... } }) */
function toQueryRecord(queryOrOptions?: Record<string, unknown>): Record<string, unknown> {
  if (!queryOrOptions || typeof queryOrOptions !== 'object') {
    return {};
  }
  const nested = queryOrOptions['params'];
  if (
    nested !== undefined &&
    nested !== null &&
    typeof nested === 'object' &&
    !Array.isArray(nested)
  ) {
    return nested as Record<string, unknown>;
  }
  return queryOrOptions;
}

function buildQueryString(query: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    u.append(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  const text = await res.text();
  if (!text) {
    return undefined as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(`非 JSON 响应 (${res.status}): ${text.slice(0, 200)}`);
  }
}

async function responseError(res: Response): Promise<Error> {
  const text = await res.text().catch(() => res.statusText);
  if (text) {
    try {
      const payload = JSON.parse(text) as { detail?: unknown; message?: unknown };
      const detail = payload.detail ?? payload.message;
      if (typeof detail === 'string' && detail.trim()) {
        return new Error(detail);
      }
    } catch {
      // 非 JSON 错误响应直接使用原文。
    }
  }
  return new Error(text || `HTTP ${res.status}`);
}

export async function get<T>(url: string, queryOrOptions?: Record<string, unknown>): Promise<T> {
  const q = toQueryRecord(queryOrOptions);
  const qs = buildQueryString(q);
  const res = await fetch(`${url}${qs}`, {
    method: 'GET',
    headers: jsonHeaders(),
  });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
  if (!res.ok) {
    throw await responseError(res);
  }
  return parseResponse<T>(res);
}

export async function post<T>(url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: jsonHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
  if (!res.ok) {
    throw await responseError(res);
  }
  return parseResponse<T>(res);
}

export async function put<T>(url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
  if (!res.ok) {
    throw await responseError(res);
  }
  return parseResponse<T>(res);
}

export async function del<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
  if (!res.ok) {
    throw await responseError(res);
  }
  return parseResponse<T>(res);
}
